import React, { useEffect, useLayoutEffect, useState } from "react";
import "./styles.css";

const fastImage = (filename) => `/images/fast/${filename}`;

const photos = [
  {
    src: fastImage("gallery-retaining-4.jpg"),
    alt: "Tiered concrete sleeper retaining wall with landscaped garden beds",
  },
  {
    src: fastImage("gallery-retaining-1.jpg"),
    alt: "Curved retaining wall beside a landscaped lawn",
  },
  {
    src: fastImage("gallery-retaining-2.jpg"),
    alt: "Concrete sleeper retaining wall with steel posts",
  },
  {
    src: fastImage("gallery-retaining-3.jpg"),
    alt: "Finished retaining wall beside a paved outdoor area",
  },
  {
    src: fastImage("gallery-new-01.jpg"),
    alt: "Boundary fencing installed above timber sleeper retaining",
  },
  {
    src: fastImage("gallery-new-05.jpg"),
    alt: "White boundary fence with retaining wall detail",
  },
  {
    src: fastImage("gallery-new-07.jpg"),
    alt: "Residential retaining wall and fence construction detail",
  },
  {
    src: fastImage("gallery-new-08.jpg"),
    alt: "Completed fence and sleeper wall along a sloped block",
  },
  {
    src: fastImage("gallery-new-09.jpg"),
    alt: "Colorbond boundary fencing with retaining base",
  },
  {
    src: fastImage("gallery-new-10.jpg"),
    alt: "New retaining wall and fence beside residential boundary",
  },
  {
    src: fastImage("gallery-new-11.jpg"),
    alt: "Finished fencing and retaining wall on a backyard boundary",
  },
  {
    src: fastImage("gallery-new-12.jpg"),
    alt: "Retaining wall and fence installation around a residential property",
  },
  {
    src: fastImage("gallery-new-13.jpg"),
    alt: "Clean boundary fencing with timber retaining sleepers",
  },
  {
    src: fastImage("gallery-new-14.jpg"),
    alt: "Retaining and fencing project with neat site finish",
  },
  {
    src: fastImage("wall4.jpg"),
    alt: "Concrete sleeper retaining wall under construction beside a driveway",
  },
  {
    src: fastImage("wall5.jpg"),
    alt: "Residential retaining wall and drainage preparation",
  },
  {
    src: fastImage("wall1.jpg"),
    alt: "Retaining wall site works beside a new home",
  },
  {
    src: fastImage("wall3.jpg"),
    alt: "Concrete sleeper wall detail with drainage preparation",
  },
  {
    src: fastImage("gallery-new-02.jpg"),
    alt: "Boundary fence panels with retaining wall below",
  },
  {
    src: fastImage("gallery-new-03.jpg"),
    alt: "Timber sleeper retaining wall supporting new fencing",
  },
  {
    src: fastImage("gallery-new-04.jpg"),
    alt: "Residential fencing and retaining wall project",
  },
  {
    src: fastImage("gallery-new-06.jpg"),
    alt: "Completed fencing and retaining project on sloped ground",
  },
];

const services = [
  {
    title: "Retaining walls",
    text: "Concrete sleeper, timber sleeper and practical wall systems for sloped sites, boundaries, gardens and outdoor living areas.",
    features: ["Site levels and set-out", "Concrete or timber sleepers", "Clean finishing around paths and gardens"],
  },
  {
    title: "Excavation and drainage",
    text: "Preparation work that gives the finished wall a better base, with drainage details planned before the sleepers go in.",
    features: ["Site preparation", "Ag pipe and gravel backfill", "Access and spoil coordination"],
  },
  {
    title: "Fencing and gates",
    text: "Boundary fencing, Colorbond, gates and finishing work delivered alongside the retaining wall when the site needs both.",
    features: ["Boundary fencing", "Colorbond options", "Gate and return details"],
  },
  {
    title: "Approval support",
    text: "Clear guidance around engineering, certificates and council requirements where the height or location of the wall calls for it.",
    features: ["Practical advice", "Engineer coordination", "Council pathway guidance"],
  },
];

const processSteps = [
  {
    number: "01",
    title: "Inspect",
    text: "We look at access, levels, soil, boundaries and drainage before recommending the right wall and fence approach.",
  },
  {
    number: "02",
    title: "Prepare",
    text: "Excavation, set-out and drainage are planned together so the site is ready before installation starts.",
  },
  {
    number: "03",
    title: "Build",
    text: "Posts, sleepers, backfill and fencing are installed with clean lines and practical sequencing.",
  },
  {
    number: "04",
    title: "Finish",
    text: "The project is checked, tidied and handed over with a focus on a neat, usable outdoor space.",
  },
];

const googleReviewFallback = {
  placeName: "Wollongong Fencing & Gates",
  rating: 5,
  totalReviews: 48,
  googleMapsUri: "https://www.google.com/search?q=Wollongong+fencing+and+gates+reviews",
  reviews: [
    {
      author: "Nat",
      rating: 5,
      text: "Always does a fantastic job, we highly recommended him.",
      relativeTime: "Google review excerpt",
      publishedAt: "",
    },
    {
      author: "Rachael",
      rating: 5,
      text: "Very easy to deal with and reliable for quotes.",
      relativeTime: "Google review excerpt",
      publishedAt: "",
    },
    {
      author: "Charles",
      rating: 5,
      text: "Outstanding work by this hard working and budget friendly team.",
      relativeTime: "Google review excerpt",
      publishedAt: "",
    },
  ],
};

const phoneNumber = "0415 505 106";
const phoneHref = "tel:+61415505106";

const renderStars = (rating) => {
  const filledStars = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));

  return Array.from({ length: 5 }, (_, index) => (
    <span className={index < filledStars ? "star filled" : "star"} key={index}>
      {"\u2605"}
    </span>
  ));
};

const formatReviewDate = (publishedAt, relativeTime) => {
  if (relativeTime) {
    return relativeTime;
  }

  if (!publishedAt) {
    return "Google review";
  }

  const reviewDate = new Date(publishedAt);

  if (Number.isNaN(reviewDate.getTime())) {
    return "Google review";
  }

  return new Intl.DateTimeFormat("en-AU", {
    month: "short",
    year: "numeric",
  }).format(reviewDate);
};

const getInitials = (name) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const isGenericReviewAuthor = (author = "") =>
  ["customer", "google reviewer"].includes(author.trim().toLowerCase());

const preloadPhoto = (index) => {
  if (typeof window === "undefined") {
    return;
  }

  const photo = photos[index];

  if (!photo) {
    return;
  }

  const image = new window.Image();
  image.src = photo.src;
};

const preloadNearbyPhotos = (index) => {
  if (index === null) {
    return;
  }

  preloadPhoto(index);
  preloadPhoto((index + 1) % photos.length);
  preloadPhoto((index - 1 + photos.length) % photos.length);
};

export default function App() {
  const [currentPhoto, setCurrentPhoto] = useState(null);
  const [googleReviews, setGoogleReviews] = useState(googleReviewFallback);
  const [reviewsStatus, setReviewsStatus] = useState("ready");

  useLayoutEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash.slice(1);

      if (!hash) {
        return;
      }

      document.getElementById(hash)?.scrollIntoView({
        block: "start",
        behavior: "auto",
      });
    };

    const timer = window.setTimeout(scrollToHash, 0);
    window.addEventListener("hashchange", scrollToHash);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("hashchange", scrollToHash);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadGoogleReviews = async () => {
      try {
        const response = await fetch("/.netlify/functions/google-reviews", {
          headers: {
            Accept: "application/json",
          },
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          const error = new Error(data.message || "Google reviews are unavailable.");
          error.needsSetup = data.needsSetup || response.status === 404;
          throw error;
        }

        if (!isMounted) {
          return;
        }

        const reviews = Array.isArray(data.reviews)
          ? data.reviews.filter((review) => review.text)
          : [];

        if (reviews.length > 0) {
          setGoogleReviews({
            ...googleReviewFallback,
            placeName: data.placeName || googleReviewFallback.placeName,
            rating: data.rating ?? googleReviewFallback.rating,
            totalReviews: data.totalReviews ?? googleReviewFallback.totalReviews,
            googleMapsUri: data.googleMapsUri || googleReviewFallback.googleMapsUri,
            reviews,
          });
        }

        setReviewsStatus("ready");
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setReviewsStatus("ready");
      }
    };

    loadGoogleReviews();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const preloadGallery = () => {
      photos.forEach((_, index) => preloadPhoto(index));
    };

    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(preloadGallery, { timeout: 1500 });

      return () => window.cancelIdleCallback(idleId);
    }

    const timer = window.setTimeout(preloadGallery, 300);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (currentPhoto === null) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    preloadNearbyPhotos(currentPhoto);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setCurrentPhoto(null);
      }

      if (event.key === "ArrowRight") {
        setCurrentPhoto((index) => {
          const nextIndex = (index + 1) % photos.length;
          preloadNearbyPhotos(nextIndex);
          return nextIndex;
        });
      }

      if (event.key === "ArrowLeft") {
        setCurrentPhoto((index) => {
          const prevIndex = (index - 1 + photos.length) % photos.length;
          preloadNearbyPhotos(prevIndex);
          return prevIndex;
        });
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [currentPhoto]);

  const openPhoto = (index) => {
    preloadNearbyPhotos(index);
    setCurrentPhoto(index);
  };

  const nextPhoto = (event) => {
    event.stopPropagation();
    setCurrentPhoto((index) => {
      const nextIndex = index === null ? 0 : (index + 1) % photos.length;
      preloadNearbyPhotos(nextIndex);
      return nextIndex;
    });
  };

  const prevPhoto = (event) => {
    event.stopPropagation();
    setCurrentPhoto((index) => {
      const prevIndex =
        index === null ? photos.length - 1 : (index - 1 + photos.length) % photos.length;
      preloadNearbyPhotos(prevIndex);
      return prevIndex;
    });
  };

  const visibleReviews = googleReviews.reviews.filter(
    (review) => review.text && !isGenericReviewAuthor(review.author)
  );

  return (
    <div className="site">
      <header className="navbar">
        <a className="brand" href="#top" aria-label="Illawarra Retaining Walls and Fencing home">
          <span className="logoFrame" aria-hidden="true">
            <img className="siteLogo" src="/images/option2-mark.png" alt="" />
          </span>
          <span className="brandText">
            <strong>Illawarra Retaining Walls & Fencing</strong>
            <span>Division of Wollongong Fencing & Gates</span>
          </span>
        </a>

        <nav aria-label="Main navigation">
          <a href="#services">Services</a>
          <a href="#about">About</a>
          <a href="#quote">Quote</a>
          <a href="#gallery">Photos</a>
          <a href="#reviews">Reviews</a>
          <a className="navCall" href={phoneHref}>Call Now</a>
        </nav>
      </header>

      <main id="top">
        <section
          className="hero"
          aria-labelledby="hero-title"
          style={{
            backgroundImage:
              'linear-gradient(90deg, rgba(0, 0, 0, 0.96), rgba(0, 0, 0, 0.68), rgba(0, 0, 0, 0.1)), url("/images/option2-logo.png")',
          }}
        >
          <div className="heroContent">
            <p className="eyebrow">Wollongong, Shellharbour, Kiama and the Illawarra</p>
            <h1 id="hero-title">
              Illawarra retaining walls
              <br />
              and fencing
            </h1>
            <p className="heroText">
              Retaining walls, sleepers, excavation, drainage and fencing handled by a local crew
              from quote through clean finish.
            </p>

            <div className="heroActions">
              <a className="btn primary" href="#quote">
                Request a Quote
              </a>
              <a className="btn light" href={phoneHref}>
                Call Now
              </a>
              <a className="btn secondary" href="#gallery">
                View Work
              </a>
            </div>
          </div>
        </section>

        <section className="confidenceBar" aria-label="Service highlights">
          <div className="sectionInner confidenceGrid">
            <div>
              <span>01</span>
              <strong>Designed around the site</strong>
              <p>Levels, access and drainage guide the build before materials are ordered.</p>
            </div>
            <div>
              <span>02</span>
              <strong>One coordinated crew</strong>
              <p>Retaining, excavation and fencing can be handled together with fewer handovers.</p>
            </div>
            <div>
              <span>03</span>
              <strong>Local Illawarra service</strong>
              <p>Clear communication for homeowners, builders and property managers.</p>
            </div>
          </div>
        </section>

        <section id="services" className="section servicesSection">
          <div className="sectionInner">
            <div className="sectionHeader">
              <p className="eyebrow">Services</p>
              <h2>Practical retaining and fencing work with the details handled properly.</h2>
              <p>
                Choose a single service or have the wall, drainage and fence coordinated as one
                project so the finished outdoor space feels resolved.
              </p>
            </div>

            <div className="serviceGrid">
              {services.map((service) => (
                <article className="serviceCard" key={service.title}>
                  <h3>{service.title}</h3>
                  <p>{service.text}</p>
                  <ul>
                    {service.features.map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section showcaseSection">
          <div className="sectionInner showcaseGrid">
            <div className="showcaseCopy">
              <p className="eyebrow">Project ready</p>
              <h2>Walls, drainage and fencing planned as one outdoor system.</h2>
              <p>
                A retaining wall is only as good as the preparation around it. The team considers
                the fall of the block, access for machinery, drainage paths, boundary lines and the
                finish you want to see every day.
              </p>
              <div className="materialList" aria-label="Common project inclusions">
                <span>Concrete sleepers</span>
                <span>Timber sleepers</span>
                <span>Colorbond fencing</span>
                <span>Ag pipe drainage</span>
                <span>Gravel backfill</span>
                <span>Gate returns</span>
              </div>
            </div>

            <div className="showcaseImages" aria-label="Selected project photos">
              <img className="showcaseLarge" src={fastImage("wall4.jpg")} alt="" />
              <img src={fastImage("gallery-new-01.jpg")} alt="" />
              <img src={fastImage("gallery-retaining-2.jpg")} alt="" />
            </div>
          </div>
        </section>

        <section id="process" className="section processSection">
          <div className="sectionInner">
            <div className="sectionHeader">
              <p className="eyebrow">Process</p>
              <h2>A clearer path from first inspection to finished wall.</h2>
            </div>

            <div className="processGrid">
              {processSteps.map((step) => (
                <article className="processStep" key={step.number}>
                  <span>{step.number}</span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="about" className="section aboutSection">
          <div className="sectionInner split">
            <div className="aboutCopy">
              <p className="eyebrow">About</p>
              <h2>Local retaining wall and fencing work with the whole site in mind.</h2>
              <p>
                Illawarra Retaining Walls & Fencing helps homeowners, builders and property
                managers across Wollongong, Shellharbour, Kiama and the wider Illawarra create
                practical outdoor spaces on sloped blocks, boundaries, gardens and driveways.
              </p>
              <p>
                As a division of Wollongong Fencing & Gates, the business brings retaining,
                drainage and fencing work together under one local team. That means clearer
                sequencing, cleaner communication and fewer gaps between trades.
              </p>
            </div>

            <aside className="supportPanel" aria-label="Included project support">
              <h3>Included support</h3>
              <ul>
                <li>Site inspections and clear quoting</li>
                <li>Concrete and timber sleeper options</li>
                <li>Excavation, drainage and backfill planning</li>
                <li>Boundary fencing, Colorbond and gates</li>
                <li>Engineering and council guidance where needed</li>
              </ul>
            </aside>
          </div>
        </section>

        <section id="quote" className="section quoteSection">
          <div className="sectionInner quoteGrid">
            <div className="quoteIntro">
              <p className="eyebrow">Request quote</p>
              <h2>Tell us about the wall, fence or site work you need.</h2>
              <p>
                Share your location, a short description and a few photos if you have them. We will
                get back to you with the next step.
              </p>

              <a className="callOption" href={phoneHref}>
                <span>Call now</span>
                <strong>{phoneNumber}</strong>
              </a>

              <div className="quoteNotes">
                <strong>Helpful details to include</strong>
                <p>Approximate wall length or height, access notes, drainage concerns and whether fencing is required.</p>
              </div>
            </div>

            <form
              className="quoteForm"
              action="https://formsubmit.co/wollongongfencing2500@gmail.com"
              method="POST"
              encType="multipart/form-data"
              onSubmit={() =>
                window.alert("Thank you, we will get in touch as soon as possible.")
              }
            >
              <input type="hidden" name="_subject" value="New Quote Request" />
              <input type="hidden" name="_captcha" value="false" />

              <label>
                Full name*
                <input type="text" name="name" autoComplete="name" required />
              </label>
              <label>
                Location / suburb*
                <input type="text" name="location" autoComplete="address-level2" required />
              </label>
              <label>
                Email address*
                <input type="email" name="email" autoComplete="email" required />
              </label>
              <label>
                Phone number*
                <input type="tel" name="phone" autoComplete="tel" required />
              </label>
              <label className="fullField">
                Project details*
                <textarea name="project_details" required />
              </label>
              <label className="uploadLabel fullField">
                Upload photos
                <input type="file" name="attachment" accept="image/*" multiple />
              </label>

              <button type="submit" className="btn primary fullField">
                Send Quote Request
              </button>
            </form>
          </div>
        </section>

        <section id="gallery" className="section gallerySection">
          <div className="sectionInner">
            <div className="sectionHeader galleryHeader">
              <div>
                <p className="eyebrow">Gallery</p>
                <h2>Recent retaining wall and fencing projects.</h2>
              </div>
              <a className="textLink" href="#quote">
                Start a similar project
              </a>
            </div>

            <div className="galleryGrid">
              {photos.map((photo, index) => (
                <button
                  className={`galleryButton${index === 0 ? " featured" : ""}`}
                  key={photo.src}
                  type="button"
                  onClick={() => openPhoto(index)}
                  onFocus={() => preloadNearbyPhotos(index)}
                  onMouseEnter={() => preloadNearbyPhotos(index)}
                  aria-label={`Open photo ${index + 1}: ${photo.alt}`}
                >
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    loading={index < 8 ? "eager" : "lazy"}
                    decoding="async"
                    fetchPriority={index < 4 ? "high" : "auto"}
                  />
                </button>
              ))}
            </div>
          </div>
        </section>

        <section id="reviews" className="section reviewsSection">
          <div className="sectionInner reviewsLayout">
            <div className="reviewsIntro">
              <p className="eyebrow">Google reviews</p>
              <h2>Feedback from local customers after the work is done.</h2>
              <p>
                Review highlights are shown directly on this page so homeowners can see what other
                Illawarra customers have said before starting a project.
              </p>

              <div className="ratingPanel" aria-label="Google review summary">
                <span className="reviewSource">Google rating</span>
                {googleReviews.rating ? (
                  <>
                    <strong>{Number(googleReviews.rating).toFixed(1)}</strong>
                    <div
                      className="starRow"
                      aria-label={`${Number(googleReviews.rating).toFixed(1)} out of 5 stars`}
                    >
                      {renderStars(googleReviews.rating)}
                    </div>
                    {googleReviews.totalReviews ? (
                      <span>{googleReviews.totalReviews} customer reviews</span>
                    ) : null}
                  </>
                ) : (
                  <>
                    <strong>Google</strong>
                    <span>Customer review highlights</span>
                  </>
                )}
                <a
                  className="textLink"
                  href={googleReviews.googleMapsUri}
                  target="_blank"
                  rel="noreferrer"
                >
                  View Google reviews
                </a>
              </div>
            </div>

            <div className="reviewGrid" aria-live={reviewsStatus === "loading" ? "polite" : "off"}>
              {reviewsStatus === "loading"
                ? [0, 1, 2].map((item) => (
                    <article className="reviewCard reviewSkeleton" key={item}>
                      <span />
                      <span />
                      <span />
                    </article>
                  ))
                : null}

              {visibleReviews.map((review, index) => (
                <article className="reviewCard" key={`${review.author}-${review.publishedAt}-${index}`}>
                  <header>
                    <div className="reviewAvatar" aria-hidden="true">
                      {getInitials(review.author || "Google reviewer")}
                    </div>
                    <div>
                      <h3>{review.author || "Google reviewer"}</h3>
                      <p>{formatReviewDate(review.publishedAt, review.relativeTime)}</p>
                    </div>
                  </header>
                  <div
                    className="starRow compact"
                    aria-label={`${Number(review.rating || 5).toFixed(0)} out of 5 stars`}
                  >
                    {renderStars(review.rating || 5)}
                  </div>
                  <blockquote>{review.text}</blockquote>
                </article>
              ))}

              {reviewsStatus !== "loading" && visibleReviews.length === 0 ? (
                <div className="reviewEmpty">
                  <strong>Customer reviews from Google will appear here shortly.</strong>
                  <p>
                    You can still open the Google profile to read the latest customer feedback.
                  </p>
                  <a
                    className="textLink"
                    href={googleReviews.googleMapsUri}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open Google reviews
                  </a>
                </div>
              ) : null}
            </div>
          </div>
        </section>

      </main>

      <a className="floatingCall" href={phoneHref} aria-label={`Call now on ${phoneNumber}`}>
        <span>Call</span>
        <strong>{phoneNumber}</strong>
      </a>

      <footer>
        <div className="footerInner">
          <div>
            <strong>Illawarra Retaining Walls & Fencing</strong>
            <p>Division of Wollongong Fencing & Gates</p>
          </div>
          <a href={phoneHref}>{phoneNumber}</a>
        </div>
      </footer>

      {currentPhoto !== null && (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Project photo preview"
          onClick={() => setCurrentPhoto(null)}
        >
          <button className="lightboxClose" type="button" onClick={() => setCurrentPhoto(null)}>
            Close
          </button>
          <button className="lightboxNav prev" type="button" onClick={prevPhoto}>
            Previous
          </button>
          <figure onClick={(event) => event.stopPropagation()}>
            <img
              src={photos[currentPhoto].src}
              alt={photos[currentPhoto].alt}
              loading="eager"
              decoding="sync"
              fetchPriority="high"
            />
            <figcaption>{photos[currentPhoto].alt}</figcaption>
          </figure>
          <button className="lightboxNav next" type="button" onClick={nextPhoto}>
            Next
          </button>
        </div>
      )}
    </div>
  );
}
